import { Controller } from "@hotwired/stimulus";
import { dsConfirm } from "../../utils/ds_confirm";

// Connects to data-controller="job-status"
export default class extends Controller {
  static targets = [
    "statusBadge",
    "progressBar",
    "progressText",
    "actionButtons",
  ];
  static values = {
    jobId: String,
    jobType: String,
    status: String,
    pollUrl: String,
    refreshInterval: { type: Number, default: 3000 },
  };

  connect() {
    console.log(
      `Job Status controller connected for ${this.jobTypeValue} job ${this.jobIdValue}`,
    );

    if (this.shouldPoll()) {
      this.startPolling();
    }

    this.updateDisplay();
  }

  disconnect() {
    this.stopPolling();
  }

  shouldPoll() {
    return ["pending", "processing", "queued"].includes(this.statusValue);
  }

  startPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }

    this.pollInterval = setInterval(() => {
      this.fetchJobStatus();
    }, this.refreshIntervalValue);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  async fetchJobStatus() {
    try {
      const response = await fetch(this.pollUrlValue, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.updateJobStatus(data);
    } catch (error) {
      console.error("Error fetching job status:", error);
      // Stop polling on error to avoid spam
      this.stopPolling();
    }
  }

  updateJobStatus(data) {
    const oldStatus = this.statusValue;
    this.statusValue = data.status;

    // Update progress if available
    if (data.progress_percentage !== undefined) {
      this.updateProgress(data.progress_percentage);
    }

    // Update display
    this.updateDisplay();

    // Handle status changes
    if (oldStatus !== data.status) {
      this.handleStatusChange(oldStatus, data.status, data);
    }

    // Stop polling if job is complete
    if (!this.shouldPoll()) {
      this.stopPolling();
    }
  }

  updateProgress(percentage) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`;
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percentage}%`;
    }
  }

  updateDisplay() {
    this.updateStatusBadge();
    this.updateActionButtons();
  }

  updateStatusBadge() {
    if (!this.hasStatusBadgeTarget) return;

    const badge = this.statusBadgeTarget;
    const status = this.statusValue;

    // Remove all status classes
    badge.classList.remove(
      "bg-gray-100",
      "text-gray-800",
      "bg-yellow-100",
      "text-yellow-800",
      "bg-blue-100",
      "text-blue-800",
      "bg-cta-shared",
      "text-cta-darker",
      "bg-red-100",
      "text-red-800",
    );

    // Add appropriate classes based on status
    const statusClasses = this.getStatusClasses(status);
    badge.classList.add(...statusClasses.split(" "));
    badge.textContent = this.getStatusText(status);

    // Add animation for processing states
    if (["pending", "processing", "queued"].includes(status)) {
      badge.classList.add("animate-pulse");
    } else {
      badge.classList.remove("animate-pulse");
    }
  }

  updateActionButtons() {
    if (!this.hasActionButtonsTarget) return;

    const container = this.actionButtonsTarget;
    const status = this.statusValue;

    // Clear existing buttons
    container.innerHTML = "";

    // Add appropriate buttons based on status
    const buttons = this.getActionButtons(status);
    buttons.forEach((button) => {
      container.appendChild(button);
    });
  }

  getStatusClasses(status) {
    const classes = {
      pending: "bg-yellow-100 text-yellow-800",
      queued: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-cta-shared text-cta-darker",
      failed: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  }

  getStatusText(status) {
    const texts = {
      pending: "En attente",
      queued: "En file",
      processing: "En cours",
      completed: "Terminé",
      failed: "Échec",
      cancelled: "Annulé",
    };
    return texts[status] || status;
  }

  getActionButtons(status) {
    const buttons = [];

    switch (status) {
      case "pending":
      case "queued":
        buttons.push(
          this.createButton(
            "Annuler",
            "cancel",
            "bg-red-100 text-red-700 hover:bg-red-200",
          ),
        );
        break;

      case "processing":
        buttons.push(
          this.createButton(
            "Annuler",
            "cancel",
            "bg-red-100 text-red-700 hover:bg-red-200",
          ),
        );
        break;

      case "completed":
        buttons.push(
          this.createButton(
            "Voir",
            "view",
            "bg-blue-100 text-blue-700 hover:bg-blue-200",
          ),
        );
        buttons.push(
          this.createButton(
            "Télécharger",
            "download",
            "bg-cta-shared text-cta-darker hover:bg-cta-shared-hover",
          ),
        );
        break;

      case "failed":
        buttons.push(
          this.createButton(
            "Réessayer",
            "retry",
            "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
          ),
        );
        buttons.push(
          this.createButton(
            "Voir erreurs",
            "view-errors",
            "bg-red-100 text-red-700 hover:bg-red-200",
          ),
        );
        break;
    }

    return buttons;
  }

  createButton(text, action, classes) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `px-3 py-1 rounded text-xs font-medium transition-colors ${classes}`;
    button.textContent = text;
    button.dataset.action = `click->job-status#${action}`;
    return button;
  }

  handleStatusChange(oldStatus, newStatus, data) {
    console.log(
      `Job ${this.jobIdValue} status changed from ${oldStatus} to ${newStatus}`,
    );

    // Show notifications for important status changes
    switch (newStatus) {
      case "completed":
        this.showNotification("Génération terminée avec succès !", "success");
        break;

      case "failed":
        this.showNotification(
          `Échec de la génération: ${data.error_message || "Erreur inconnue"}`,
          "error",
        );
        break;

      case "processing":
        if (oldStatus === "pending" || oldStatus === "queued") {
          this.showNotification("Génération démarrée", "info");
        }
        break;
    }

    // Dispatch custom event for other components to listen to
    this.dispatch("statusChanged", {
      detail: {
        jobId: this.jobIdValue,
        jobType: this.jobTypeValue,
        oldStatus: oldStatus,
        newStatus: newStatus,
        data: data,
      },
    });
  }

  showNotification(message, type) {
    if (window.Toast) {
      window.Toast.show(message, type);
    } else {
      // Fallback to alert if toast is not available
      alert(message);
    }
  }

  // Action methods
  async cancel() {
    if (await dsConfirm("Êtes-vous sûr de vouloir annuler cette tâche ?", { tone: "danger" })) {
      this.performAction("cancel");
    }
  }

  async retry() {
    if (await dsConfirm("Voulez-vous relancer cette génération ?")) {
      this.performAction("retry");
    }
  }

  view() {
    this.performAction("view");
  }

  download() {
    this.performAction("download");
  }

  viewErrors() {
    this.performAction("view-errors");
  }

  async performAction(action) {
    try {
      const response = await fetch(`${this.pollUrlValue}/${action}`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          job_id: this.jobIdValue,
          job_type: this.jobTypeValue,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        this.handleActionSuccess(action, data);
      } else {
        this.handleActionError(action, data.error || "Action failed");
      }
    } catch (error) {
      console.error(`Error performing action ${action}:`, error);
      this.handleActionError(action, error.message);
    }
  }

  handleActionSuccess(action, data) {
    switch (action) {
      case "cancel":
        this.statusValue = "cancelled";
        this.updateDisplay();
        this.showNotification("Tâche annulée", "info");
        break;

      case "retry":
        this.statusValue = "pending";
        this.updateDisplay();
        this.startPolling();
        this.showNotification("Génération relancée", "info");
        break;

      case "view":
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        }
        break;

      case "download":
        if (data.download_url) {
          window.open(data.download_url, "_blank");
        }
        break;

      case "view-errors":
        if (data.errors) {
          this.showErrorModal(data.errors);
        }
        break;
    }
  }

  handleActionError(action, error) {
    this.showNotification(
      `Erreur lors de l'action ${action}: ${error}`,
      "error",
    );
  }

  showErrorModal(errors) {
    // Create a simple error modal
    const modal = document.createElement("div");
    modal.className =
      "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50";
    modal.innerHTML = `
      <div class="bg-white rounded-md p-6 max-w-md w-full mx-4">
        <div class="flex items-center mb-4">
          <svg class="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h3 class="text-lg font-semibold text-gray-900">Erreurs de génération</h3>
        </div>
        <div class="mb-4">
          <ul class="list-disc list-inside text-sm text-gray-700 space-y-1">
            ${errors.map((error) => `<li>${this.escapeHtml(error)}</li>`).join("")}
          </ul>
        </div>
        <div class="flex justify-end">
          <button type="button" onclick="this.closest('.fixed').remove()" class="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded">
            Fermer
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close on background click
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
